import { useMemo } from 'react';
import { useCustomizationContext } from './useCardCustomization';
import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useFeatureValue } from '@shared/core/featureFlags';

export const usePrintCardsProductQuantityOptions = (currentQuantity?: number, quantityChoicesOverride?: number[]) => {
  const [draft] = useCustomizationContext(draft => draft);
  const minorUnitsToDollars = useMinorUnitsToPriceString();
  const { priceCalculator, activeProduct } = useStationeryProductPricingContext();
  const { value: printEnableSampleOrderValue } = useFeatureValue('printEnableSampleOrder', { skip: activeProduct.productType !== 'stationeryCard' });

  const options = useMemo(() => {
    const entries = quantityChoicesOverride ?? activeProduct.supportedQuantities;

    // Add '4' quantity option if printEnableSampleOrder flag is on,
    // but only if we are viewing a paper card.
    if (printEnableSampleOrderValue === 'on' && !entries.includes(4)) {
      entries.unshift(4);
    }

    return entries
      .reduce((acc, quantity) => {
        const pricingForQuantity = priceCalculator.getProductBasePrice(activeProduct.id, quantity);
        const totalPrice = pricingForQuantity.totalPriceInMinorUnits;
        const unitPrice = pricingForQuantity.unitPriceInMinorUnits;

        const pricePerCardString = minorUnitsToDollars(unitPrice, { canShowFreeLabel: true });
        const totalPriceString = minorUnitsToDollars(totalPrice, { canShowFreeLabel: true });

        acc.push({
          key: quantity.toString(),
          label: `${quantity} items at ${pricePerCardString} each`,
          value: quantity,
          pricePerCardString,
          totalPriceString,
        });
        return acc;
      }, [] as Array<{ key: string; label: string; value: number; pricePerCardString: string; totalPriceString: string }>)
      .sort((a, b) => {
        return a.value - b.value;
      });
  }, [quantityChoicesOverride, activeProduct, printEnableSampleOrderValue, priceCalculator, minorUnitsToDollars]);

  const selectedOption = useMemo(() => {
    return options.find(opt => opt.value === (currentQuantity ?? draft.config.customizations.quantity)) ?? options[0];
  }, [options, currentQuantity, draft.config.customizations.quantity]);

  return { options, selectedOption };
};
